// src/rms/ads/useAdSurfaces.js
// ============================================================================
// One selection pass for every ad surface on screen
// ============================================================================
// The two masthead units and the rail units are chosen together rather than
// independently. Selecting separately let the same advertiser appear in the
// banner and again in the rail, which reads as a bug and double-charges an
// impression the advertiser will not pay for twice.
//
// Owned by RmsLayout and passed down, so there is exactly one rotation clock
// and one dedupe set per screen.
// ============================================================================

import { useEffect, useState } from "react";
import { selectAdStack, subscribeToRefresh, currentEpoch } from "./adEngine";
import { MASTHEAD_SLOTS, RAIL_SLOTS } from "./adInventory";
import { loadServableCampaigns } from "./campaignSource";



// 32s, deliberately above the ~30s minimum most networks require for auto
// refresh. Rotating faster produces impressions that fail the 1-second
// viewability test and, with several networks, breaches policy outright.
const ROTATION_MS = 32000;

export default function useAdSurfaces({ operator, config, enabled }) {
  const [epoch, setEpoch] = useState(currentEpoch());
  const [tick, setTick] = useState(0);
  const [pools, setPools] = useState(null);

  // Sold demand comes from the database and has to be re-read as flights start,
  // end, exhaust their budget or hit a frequency cap. Reloaded on each rotation
  // rather than on every render.
  useEffect(() => {
    if (!enabled || !operator || !config) {
      setPools(null);
      return undefined;
    }
    let cancelled = false;

    (async () => {
      const [sidebar, banner] = await Promise.all([
        loadServableCampaigns({ operatorId: operator.id, adCategory: config.adCategory, placement: "sidebar" }),
        loadServableCampaigns({ operatorId: operator.id, adCategory: config.adCategory, placement: "banner_top" }),
      ]);
      if (!cancelled) setPools({ sidebar, banner_top: banner });
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, operator, config, epoch, tick]);

  // Re-select only when the action-based frequency cap allows it.
  useEffect(() => subscribeToRefresh(setEpoch), []);

  // One timer for the whole screen. Units flipping independently is more
  // distracting than everything refreshing at once.
  useEffect(() => {
    if (!enabled) return undefined;
    const id = setInterval(() => setTick((n) => n + 1), ROTATION_MS);
    return () => clearInterval(id);
  }, [enabled]);

  if (!enabled) return { mastheads: [], rail: [] };

  // Masthead first so the leaderboard gets the strongest available demand.
  const filled = selectAdStack({
    operator,
    config,
    slotKeys: [...MASTHEAD_SLOTS, ...RAIL_SLOTS],
    nonce: epoch * 31 + tick,
    pools,
  });

  return {
    // Ordered so the two header units keep their left/right positions rather
    // than swapping when one of them fails to fill.
    mastheads: MASTHEAD_SLOTS.map((k) => filled.find((f) => f.slotKey === k) || null),
    rail: filled.filter((f) => !MASTHEAD_SLOTS.includes(f.slotKey)),
  };
}
